import React, { useState, useEffect } from 'react';
import Countdown from '../components/Countdown';
import { raceData, parseDate, formatToUserTimezone } from '../data/raceData';
import { resultsData } from '../data/resultsData';
import { qualyResultsData } from '../data/qualyResultsData';
import { teamsData } from '../data/teamsData';
import '../App.css';

const Calendar = ({ t }) => {
    const [selectedRace, setSelectedRace] = useState(null);
    const [targetSession, setTargetSession] = useState('race');

    // Determine the next upcoming race on load
    useEffect(() => {
        const now = new Date();
        const upcoming = raceData.find(r => parseDate(r.race) > now);
        if (upcoming) {
            setSelectedRace(upcoming);
        } else {
            setSelectedRace(raceData[0]);
        }
    }, []);

    // Reset to 'race' when selected race changes
    useEffect(() => {
        setTargetSession('race');
    }, [selectedRace]);

    if (!selectedRace) return <div className="loading">Loading...</div>;

    const targetDate = parseDate(selectedRace[targetSession]);

    // Helper to get driver details from teamsData dynamically
    const getDriverDetails = (driverName) => {
        const normalized = driverName.toLowerCase();
        const teamObj = teamsData.find(t =>
            t.driver1.name.toLowerCase().includes(normalized) ||
            t.driver2.name.toLowerCase().includes(normalized)
        );
        if (teamObj) {
            const isDriver1 = teamObj.driver1.name.toLowerCase().includes(normalized);
            const driverData = isDriver1 ? teamObj.driver1 : teamObj.driver2;
            return {
                driver: driverData.name,
                team: teamObj.team,
                flag: driverData.flag,
                points: null
            };
        }
        return {
            driver: driverName,
            team: '',
            flag: '',
            points: null
        };
    };

    // Get results for this session if it is a race, sprint race, or qualifying
    const raceResultsObj = resultsData[selectedRace.id];
    const qualyResultsObj = qualyResultsData[selectedRace.id];
    let sessionResults = null;
    let resultsTitle = '';

    if (raceResultsObj && (targetSession === 'race' || (targetSession === 'fp3' && selectedRace.fp3.includes('Sprint')))) {
        if (targetSession === 'race') {
            sessionResults = raceResultsObj.raceResults;
            resultsTitle = t.raceResults || 'Race Results';
        } else if (targetSession === 'fp3' && selectedRace.fp3.includes('Sprint')) {
            sessionResults = raceResultsObj.sprintResults;
            resultsTitle = t.sprintResults || 'Sprint Results';
        }
    } else if (qualyResultsObj && (targetSession === 'qualy' || (targetSession === 'fp2' && selectedRace.fp2.includes('SQ')))) {
        let driversList = null;
        if (targetSession === 'qualy') {
            driversList = qualyResultsObj.qualy;
            resultsTitle = t.qualifying || 'Qualifying';
        } else if (targetSession === 'fp2' && selectedRace.fp2.includes('SQ')) {
            driversList = qualyResultsObj.sprintQualy;
            resultsTitle = t.sprintQualy || 'Sprint Qualy';
        }
        if (driversList) {
            sessionResults = driversList.map((driverName, idx) => ({
                position: idx + 1,
                ...getDriverDetails(driverName)
            }));
        }
    }

    const sessionNames = {
        fp1: t.practice1.split(' (')[0],
        fp2: selectedRace.fp2.includes('SQ') ? t.sprintQualy.split(' (')[0] : t.practice2.split(' (')[0],
        fp3: selectedRace.fp3.includes('Sprint') ? t.sprintRace : t.practice3.split(' (')[0],
        qualy: t.qualifying,
        race: t.grandPrix
    };

    // ─── Calendar helpers ─────────────────────────────────────────────────────

    const toGCalFormat = (date) =>
        date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

    const sessionDurations = {
        fp1: 60 * 60 * 1000,
        fp2: 60 * 60 * 1000,
        fp3: 60 * 60 * 1000,
        qualy: 60 * 60 * 1000,
        race: 2 * 60 * 60 * 1000,
    };

    // Single-session: opens Google Calendar directly (one tab = allowed by browsers)
    const buildCalendarUrl = (race, sessionKey, sessionLabel) => {
        const dateStr = race[sessionKey];
        if (!dateStr) return null;
        const startDate = parseDate(dateStr);
        const endDate = new Date(startDate.getTime() + (sessionDurations[sessionKey] || 60 * 60 * 1000));
        const params = new URLSearchParams({
            action: 'TEMPLATE',
            text: `F1 2026 \u2013 ${race.gp} ${race.flag} \u2013 ${sessionLabel}`,
            dates: `${toGCalFormat(startDate)}/${toGCalFormat(endDate)}`,
            details: `Formula 1 2026 Season\n${race.gp} Grand Prix \u00b7 Round ${race.id}\n\ud83d\udccd ${race.city}\n\ud83c\udfce\ufe0f ${sessionLabel}`,
            location: race.city,
            ctz: 'UTC',
        });
        return `https://calendar.google.com/calendar/render?${params.toString()}`;
    };

    const handleAddSession = (sessionKey, sessionLabel, e) => {
        if (e) e.stopPropagation();
        const url = buildCalendarUrl(selectedRace, sessionKey, sessionLabel);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    };

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <>
            <section className="hero-section">
                <div className="hero-header">
                    <div className="hero-info">
                        <div className="next-race-badge">{t.round} {selectedRace.id}</div>
                        <h2 className="hero-gp-title">
                            {selectedRace.gp} {selectedRace.flag}
                            {selectedRace.isSprint && <span className="sprint-badge-inline">{t.sprint}</span>}
                            {selectedRace.isSuspended && <span className="suspended-badge-inline">{t.suspended}</span>}
                        </h2>
                        <h3 className="hero-circuit">{selectedRace.city}</h3>
                    </div>

                    <div className="hero-countdown-wrapper">
                        {selectedRace.isSuspended ? (
                            <div className="suspended-notice">
                                <span className="suspended-icon">⚠️</span>
                                <span className="suspended-text">{t.suspendedGPNotice}</span>
                            </div>
                        ) : (
                            <>
                                <p>{t.countdownFor} <span className="target-session-name">{sessionNames[targetSession]}</span></p>
                                <Countdown
                                    targetDate={targetDate}
                                    t={t}
                                    winner={selectedRace.winner}
                                    team={selectedRace.team}
                                    results={sessionResults}
                                    resultsTitle={resultsTitle}
                                />
                            </>
                        )}
                    </div>
                </div>

                <div className="schedule-table-container">
                    <h3>{t.selectSession}</h3>
                    {selectedRace.isSuspended ? (
                        <div className="suspended-table-placeholder">
                            <p>{t.sessionsSuspended}</p>
                        </div>
                    ) : (
                        <>
                            <p className="instruction-text">{t.clickInstruction}</p>
                            <table className="schedule-table">
                                <tbody>
                                    {/* FP1 */}
                                    <tr
                                        className={`clickable-row ${targetSession === 'fp1' ? 'active-session-row' : ''}`}
                                        onClick={() => setTargetSession('fp1')}
                                    >
                                        <td className="session-name">{t.practice1}</td>
                                        <td className="session-time">{formatToUserTimezone(selectedRace.fp1)}</td>
                                        <td className="session-cal-cell">
                                            <button
                                                className="cal-btn"
                                                title={t.addToCalendar}
                                                onClick={(e) => handleAddSession('fp1', sessionNames.fp1, e)}
                                            >
                                                📅
                                            </button>
                                        </td>
                                    </tr>
                                    {/* FP2 / Sprint Qualy */}
                                    <tr
                                        className={`clickable-row ${targetSession === 'fp2' ? 'active-session-row' : ''}`}
                                        onClick={() => setTargetSession('fp2')}
                                    >
                                        <td className="session-name">
                                            {selectedRace.fp2.includes('SQ') ? t.sprintQualy : t.practice2}
                                        </td>
                                        <td className="session-time">{formatToUserTimezone(selectedRace.fp2)}</td>
                                        <td className="session-cal-cell">
                                            <button
                                                className="cal-btn"
                                                title={t.addToCalendar}
                                                onClick={(e) => handleAddSession('fp2', sessionNames.fp2, e)}
                                            >
                                                📅
                                            </button>
                                        </td>
                                    </tr>
                                    {/* FP3 / Sprint Race */}
                                    <tr
                                        className={`clickable-row ${targetSession === 'fp3' ? 'active-session-row' : ''}`}
                                        onClick={() => setTargetSession('fp3')}
                                    >
                                        <td className="session-name">
                                            {selectedRace.fp3.includes('Sprint') ? t.sprintRace : t.practice3}
                                        </td>
                                        <td className="session-time">{formatToUserTimezone(selectedRace.fp3)}</td>
                                        <td className="session-cal-cell">
                                            <button
                                                className="cal-btn"
                                                title={t.addToCalendar}
                                                onClick={(e) => handleAddSession('fp3', sessionNames.fp3, e)}
                                            >
                                                📅
                                            </button>
                                        </td>
                                    </tr>
                                    {/* Qualifying */}
                                    <tr
                                        className={`clickable-row ${targetSession === 'qualy' ? 'active-session-row' : ''}`}
                                        onClick={() => setTargetSession('qualy')}
                                    >
                                        <td className="session-name">{t.qualifying}</td>
                                        <td className="session-time">{formatToUserTimezone(selectedRace.qualy)}</td>
                                        <td className="session-cal-cell">
                                            <button
                                                className="cal-btn"
                                                title={t.addToCalendar}
                                                onClick={(e) => handleAddSession('qualy', sessionNames.qualy, e)}
                                            >
                                                📅
                                            </button>
                                        </td>
                                    </tr>
                                    {/* Race */}
                                    <tr
                                        className={`clickable-row main-event-row ${targetSession === 'race' ? 'active-session-row' : ''}`}
                                        onClick={() => setTargetSession('race')}
                                    >
                                        <td className="session-name">{t.grandPrix}</td>
                                        <td className="session-time">{formatToUserTimezone(selectedRace.race)}</td>
                                        <td className="session-cal-cell">
                                            <button
                                                className="cal-btn cal-btn--race"
                                                title={t.addToCalendar}
                                                onClick={(e) => handleAddSession('race', sessionNames.race, e)}
                                            >
                                                📅
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                        </>
                    )}
                </div>
            </section>

            <section className="race-list">
                <h3>{t.calendar}</h3>
                <div className="grid">
                    {raceData.map((race) => {
                        const isActive = selectedRace.id === race.id;
                        return (
                            <div
                                key={race.id}
                                className={`race-card ${isActive ? 'active-card' : ''}`}
                                onClick={() => {
                                    setSelectedRace(race);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                            >
                                <div className="card-header">
                                    <span className="round-num">#{race.id}</span>
                                    <div className="flag-container">
                                        {race.isSuspended && <span className="suspended-badge">{t.suspended}</span>}
                                        <span className="flag">{race.flag}</span>
                                        {race.isSprint && <span className="sprint-badge">{t.sprint}</span>}
                                    </div>
                                </div>
                                <div className="card-body">
                                    <h4>{race.gp}</h4>
                                    <p className="venue">{race.city}</p>
                                    <button className="select-btn">{t.viewInfo}</button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
        </>
    );
};

export default Calendar;
